/**
 * Web Audio runtime bus for SFX + voice cohesion.
 *
 * Owned by /audiodirector (.claude/agents/audiodirector.md).
 *
 * Bus chain:
 *
 *   sources → masterGain ─────────────────────────→ destination
 *           ↘ reverbSend → ConvolverNode (room IR) ↗
 *
 * The shared room IR is synthesized in code (no download) — random noise with
 * exponential decay. Acoustically generic on purpose: the goal is glue, not
 * character. ~7% wet on voice unifies different ElevenLabs voices that have
 * different micro-acoustic spaces baked in. ~10% on SFX glues different packs.
 *
 * Future expansions per audiodirector spec:
 *   - Category gains (sfx/music/voice player sliders)
 *   - Master compressor + limiter for Steam playback safety
 *   - Real recorded room IR loaded from /audio/ir/shared_room.wav
 *   - AudioWorklet for sidechain ducking (voice → music dip)
 */

interface SfxFamily {
  pack: string;
  pattern: string;
  count: number;
  /** First valid index (default 0). Kenney's interface pack starts at 1. */
  startIndex?: number;
  /** Digit padding for the variant suffix (default 3, e.g. "_000"). */
  padding?: number;
}

// Semantic name → file family. Random-picks _NNN on each play.
const REGISTRY: Record<string, SfxFamily> = {
  // ── UI (kenney_interface, 1-indexed) ─────────────────────────────
  click:     { pack: 'kenney_interface', pattern: 'click',         count: 5, startIndex: 1 },
  select:    { pack: 'kenney_interface', pattern: 'select',        count: 8, startIndex: 1 },
  back:      { pack: 'kenney_interface', pattern: 'back',          count: 4, startIndex: 1 },
  confirm:   { pack: 'kenney_interface', pattern: 'confirmation',  count: 4, startIndex: 1 },
  error:     { pack: 'kenney_interface', pattern: 'error',         count: 8, startIndex: 1 },
  open:      { pack: 'kenney_interface', pattern: 'open',          count: 4, startIndex: 1 },
  close:     { pack: 'kenney_interface', pattern: 'close',         count: 4, startIndex: 1 },
  toggle:    { pack: 'kenney_interface', pattern: 'toggle',        count: 4, startIndex: 1 },
  scroll:    { pack: 'kenney_interface', pattern: 'scroll',        count: 5, startIndex: 1 },
  drop:      { pack: 'kenney_interface', pattern: 'drop',          count: 4, startIndex: 1 },
  question:  { pack: 'kenney_interface', pattern: 'question',      count: 4, startIndex: 1 },
  switch:    { pack: 'kenney_interface', pattern: 'switch',        count: 7, startIndex: 1 },
  tick:      { pack: 'kenney_interface', pattern: 'tick',          count: 3, startIndex: 1 },

  // ── Combat impacts (kenney_impact, 0-indexed) ────────────────────
  hit_soft_medium:  { pack: 'kenney_impact', pattern: 'impactSoft_medium',  count: 5 },
  hit_soft_heavy:   { pack: 'kenney_impact', pattern: 'impactSoft_heavy',   count: 5 },
  hit_metal_light:  { pack: 'kenney_impact', pattern: 'impactMetal_light',  count: 5 },
  hit_metal_medium: { pack: 'kenney_impact', pattern: 'impactMetal_medium', count: 5 },
  hit_metal_heavy:  { pack: 'kenney_impact', pattern: 'impactMetal_heavy',  count: 5 },
  hit_plate_medium: { pack: 'kenney_impact', pattern: 'impactPlate_medium', count: 5 },
  hit_plate_heavy:  { pack: 'kenney_impact', pattern: 'impactPlate_heavy',  count: 5 },
  hit_wood_medium:  { pack: 'kenney_impact', pattern: 'impactWood_medium',  count: 5 },
  hit_glass_medium: { pack: 'kenney_impact', pattern: 'impactGlass_medium', count: 5 },
  hit_bell_heavy:   { pack: 'kenney_impact', pattern: 'impactBell_heavy',   count: 5 },
  punch_medium:     { pack: 'kenney_impact', pattern: 'impactPunch_medium', count: 5 },
  punch_heavy:      { pack: 'kenney_impact', pattern: 'impactPunch_heavy',  count: 5 },

  // ── Footsteps by surface (kenney_impact, 0-indexed) ──────────────
  footstep_grass:    { pack: 'kenney_impact', pattern: 'footstep_grass',    count: 5 },
  footstep_wood:     { pack: 'kenney_impact', pattern: 'footstep_wood',     count: 5 },
  footstep_concrete: { pack: 'kenney_impact', pattern: 'footstep_concrete', count: 5 },
  footstep_snow:     { pack: 'kenney_impact', pattern: 'footstep_snow',     count: 5 },
  footstep_carpet:   { pack: 'kenney_impact', pattern: 'footstep_carpet',   count: 5 },
};

export type SfxName = keyof typeof REGISTRY;

const SFX_BASE = 'audio/sfx/processed';
const STORAGE_VOLUME_KEY = 'audio_sfx_volume';
const STORAGE_MUTED_KEY = 'audio_sfx_muted';

/* ── Synthetic room IR ────────────────────────────────────────────
 * Filtered noise with exponential decay. Real rooms damp high frequencies
 * over distance + time (air absorption), so a one-pole low-pass at ~3.5kHz
 * is applied — pure white noise IR sounds metallic/ringy at higher sends.
 * Stereo decorrelated. Acoustically generic on purpose: the goal is glue,
 * not character.
 *
 * Replace with a real IR loaded from /audio/ir/shared_room.wav when sourced. */
function makeRoomIR(ctx: AudioContext, durationSec = 0.6, decay = 3.5): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * durationSec);
  const ir = ctx.createBuffer(2, length, ctx.sampleRate);
  // One-pole LPF coefficient. α = 1 - exp(-2π·fc/sr); fc≈3.5kHz at 44.1k → ~0.39.
  const lpfAlpha = 1 - Math.exp(-2 * Math.PI * 3500 / ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    let state = 0;
    for (let i = 0; i < length; i++) {
      const noise = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      state += lpfAlpha * (noise - state);
      data[i] = state;
    }
  }
  return ir;
}

class AudioBusImpl {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  /** Convolver fed by reverbSendGain for parallel wet path. */
  private convolver: ConvolverNode | null = null;
  /** Element-source cache so we don't create a second source for the same
   *  HTMLAudioElement (Web Audio errors on that). */
  private mediaSources = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();
  private bufferCache = new Map<string, AudioBuffer>();
  private decodeInFlight = new Map<string, Promise<AudioBuffer>>();

  /** Lazy AudioContext init — must happen after a user gesture per
   *  browser autoplay policy. First playSfx() / routeVoiceElement() triggers this. */
  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctor();

      const master = ctx.createGain();
      master.gain.value = this.getVolume();
      master.connect(ctx.destination);

      // Parallel wet path: source → convolver → master (so reverb is mixed in
      // alongside dry, never replaces it). Send level set per category by the
      // caller via the second arg to playSfx / routeVoiceElement.
      const conv = ctx.createConvolver();
      conv.normalize = true;
      conv.buffer = makeRoomIR(ctx);
      conv.connect(master);

      this.ctx = ctx;
      this.masterGain = master;
      this.convolver = conv;
      return ctx;
    } catch (err) {
      console.warn('[AudioBus] AudioContext unavailable:', err);
      return null;
    }
  }

  private urlFor(family: SfxFamily, variantIndex: number): string {
    const base = import.meta.env.BASE_URL ?? '/';
    const padding = family.padding ?? 3;
    const padded = String(variantIndex).padStart(padding, '0');
    return `${base}${SFX_BASE}/${family.pack}/${family.pattern}_${padded}.mp3`;
  }

  private async loadBuffer(url: string): Promise<AudioBuffer | null> {
    const ctx = this.ensureCtx();
    if (!ctx) return null;

    const cached = this.bufferCache.get(url);
    if (cached) return cached;

    const inFlight = this.decodeInFlight.get(url);
    if (inFlight) return inFlight;

    const promise = (async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`fetch ${url} → ${res.status}`);
      const arrayBuf = await res.arrayBuffer();
      const audioBuf = await ctx.decodeAudioData(arrayBuf);
      this.bufferCache.set(url, audioBuf);
      this.decodeInFlight.delete(url);
      return audioBuf;
    })();

    this.decodeInFlight.set(url, promise);
    try {
      return await promise;
    } catch (err) {
      this.decodeInFlight.delete(url);
      console.warn(`[AudioBus] failed to load ${url}:`, err);
      return null;
    }
  }

  /** Play a SFX by semantic name. Picks a random variant.
   *  reverbSend: 0 = dry, 1 = full wet. Default 0.10 (~10% glue). */
  async playSfx(name: SfxName | string, reverbSend = 0.10): Promise<void> {
    if (this.isMuted()) return;
    const family = REGISTRY[name as SfxName];
    if (!family) {
      console.warn(`[AudioBus] unknown SFX "${name}"`);
      return;
    }

    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain || !this.convolver) return;

    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { /* next gesture will retry */ }
    }

    const start = family.startIndex ?? 0;
    const variantIndex = start + Math.floor(Math.random() * family.count);
    const url = this.urlFor(family, variantIndex);
    const buffer = await this.loadBuffer(url);
    if (!buffer) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);
    if (reverbSend > 0) {
      const send = ctx.createGain();
      send.gain.value = reverbSend;
      source.connect(send);
      send.connect(this.convolver);
    }
    source.start();
  }

  /** Wire an HTMLAudioElement into the bus so it picks up the shared room
   *  reverb. Used by playVoiceover() to glue different ElevenLabs voices
   *  into the same acoustic space.
   *  reverbSend: ~0.07 (7%) is enough for voice — preserves intelligibility
   *  but unifies the room.
   *
   *  Idempotent — calling twice on the same element returns the existing
   *  source. (Web Audio throws if you createMediaElementSource on the same
   *  element twice.) */
  routeVoiceElement(el: HTMLMediaElement, reverbSend = 0.07): void {
    const ctx = this.ensureCtx();
    if (!ctx || !this.masterGain || !this.convolver) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => { /* ignore; next gesture will retry */ });
    }

    if (this.mediaSources.has(el)) return;

    try {
      const source = ctx.createMediaElementSource(el);
      source.connect(this.masterGain);
      if (reverbSend > 0) {
        const send = ctx.createGain();
        send.gain.value = reverbSend;
        source.connect(send);
        send.connect(this.convolver);
      }
      this.mediaSources.set(el, source);
    } catch (err) {
      // Most common cause: element already routed by a previous source. Safe to ignore.
      console.warn('[AudioBus] routeVoiceElement failed:', err);
    }
  }

  /** Master volume 0.0–1.0. Persisted to localStorage. */
  setVolume(value: number): void {
    const clamped = Math.max(0, Math.min(1, value));
    try { localStorage.setItem(STORAGE_VOLUME_KEY, String(clamped)); } catch { /* private mode */ }
    if (this.masterGain) this.masterGain.gain.value = clamped;
  }

  getVolume(): number {
    try {
      const v = localStorage.getItem(STORAGE_VOLUME_KEY);
      if (v == null) return 0.7;
      const parsed = parseFloat(v);
      return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : 0.7;
    } catch {
      return 0.7;
    }
  }

  isMuted(): boolean {
    try {
      const v = localStorage.getItem(STORAGE_MUTED_KEY);
      if (v === '0') return false;
      return true;
    } catch { return true; }
  }

  setMuted(muted: boolean): void {
    try {
      localStorage.setItem(STORAGE_MUTED_KEY, muted ? '1' : '0');
    } catch { /* private mode */ }
  }
}

export const audioBus = new AudioBusImpl();

// Dev convenience: expose on window for quick testing in DevTools.
if (typeof window !== 'undefined') {
  (window as unknown as { audioBus: AudioBusImpl }).audioBus = audioBus;
}
